/** Angular Imports */
import { Component, OnInit, inject } from "@angular/core";
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";

/** rxjs Imports */
import { finalize } from "rxjs/operators";

/** Custom Services */
import { AuthenticationService } from "../../core/authentication/authentication.service";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { STANDALONE_SHARED_IMPORTS } from "app/standalone-shared.module";

/** Custom Service Zitadel */
import { AuthService } from "../../zitadel/auth.service";
import { environment } from "../../../environments/environment";

/**
 * Login form component.
 */
@Component({
  selector: "LOSx-login-form",
  templateUrl: "./login-form.component.html",
  styleUrls: ["./login-form.component.scss"],
  imports: [
    ...STANDALONE_SHARED_IMPORTS,
    FaIconComponent,
    MatProgressBar,
    MatProgressSpinner,
  ],
})
export class LoginFormComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private authenticationService = inject(AuthenticationService);
  private authService = inject(AuthService);

  /** Login form group. */
  loginForm: FormGroup;
  /** Password input field type. */
  passwordInputType: string = "password";
  /** True if loading. */
  loading = false;
  oidcServerEnabled = environment.OIDC.oidcServerEnabled;
  /** Whether remember me functionality is enabled */
  enableRememberMe = environment.enableRememberMe === true;
  /** State of the placeholder captcha checkbox (UI only, not verified server-side). */
  captchaState: "idle" | "verifying" | "verified" = "idle";

  /**
   * Creates login form.
   *
   * Initializes password input field type.
   */
  ngOnInit() {
    this.createLoginForm();
  }

  /**
   * Authenticates the user if the credentials are valid.
   */
  login() {
    this.loading = true;
    this.loginForm.disable();
    this.authenticationService
      .login(this.loginForm.value)
      .pipe(
        finalize(() => {
          this.loginForm.reset();
          this.loginForm.markAsPristine();
          // Angular Material Bug: Validation errors won't get removed on reset.
          this.loginForm.enable();
          this.loading = false;
          this.captchaState = "idle";
        }),
      )
      .subscribe();
  }

  /**
   * Authenticates the user if the credentials are valid to Zitadel.
   */

  loginOIDC() {
    this.authService.login();
  }

  getUsers() {
    this.authService.getUsers();
  }

  logout() {
    this.authService.logout();
  }

  /**
   * Ticks the placeholder captcha after a short "verifying" delay.
   */
  verifyCaptcha() {
    if (this.captchaState !== "idle") {
      return;
    }
    this.captchaState = "verifying";
    setTimeout(() => (this.captchaState = "verified"), 700);
  }

  /**
   * True when the control is invalid and the user has interacted with it.
   */
  showError(controlName: string): boolean {
    const control = this.loginForm.get(controlName);
    return !!control && control.invalid && control.touched;
  }

  /**
   * Toggles the visibility of the password input field.
   *
   * Changes the input type between 'password' and 'text'.
   */
  togglePasswordVisibility() {
    this.passwordInputType =
      this.passwordInputType === "password" ? "text" : "password";
  }

  /**
   * Creates login form with validation rules.
   */
  private createLoginForm() {
    this.loginForm = this.formBuilder.group({
      username: ["", Validators.required],
      password: ["", [Validators.required, Validators.minLength(8)]],
      remember: false,
    });
  }

  /**
   * Returns the appropriate error message for the specified form control.
   *
   * @param {string} controlName - The name of the form control.
   * @returns {string} - The error message.
   */

  getErrorMessage(controlName: string): string {
    const control = this.loginForm.get(controlName);
    if (control?.hasError("required")) {
      return "This field is required";
    } else if (control?.hasError("minlength")) {
      return `Minimum length is ${control.errors?.minlength.requiredLength}`;
    }
    return "";
  }
}
